// @ts-nocheck
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import momentTz from 'moment-timezone';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, TableRow, TableCell, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';

import {
  FAntdTimePicker,
  FHButton,
  FHAntdDateTimePicker,
  FHInputField,
  FHSelect,
} from '../../base';
import { timesheetActions } from '../../../store';
import {
  areUserRolesIn,
  disabledHoursAndMinutesForToday,
  getAllInvalidFieldNames,
} from '../../../helpers';
import { getUtcOffset } from '../../../utils/common';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY2,
  FORTA_GRAY4,
  FORTA_WHITE,
} from '../../../utils/colors';
import { getTimesheetSchema } from './validationSchema';
import { TIMESHEET_OPTIONS } from '../../../pages/constants';
import {
  BackgroundTypes,
  CT_TIMEZONE_AREA,
  MAX_TIMESHEET_DURATION_HOURS,
  Themes,
} from '../../../constants';
import ErrorAlertIcon from '../../../assets/images/error-alert.svg';
import { isClientOptional } from './utils';

import type {
  IClient,
  IServiceCode,
  ITimesheetTransformedData,
  SelectValueType,
} from '../../../utils/types';
import type { RootState } from '../../../store';
import type { Moment } from 'moment';

interface ITimesheetEditRowProps {
  timesheet: ITimesheetTransformedData;
  onClose: () => void;
}

const TimesheetEditRow = ({ timesheet, onClose }: ITimesheetEditRowProps) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { payPeriods, selectedPayPeriod } = useSelector((state: RootState) => state.timesheets);
  const { provider: currentProvider } = useSelector((state: RootState) => state.providers);
  const { list: clients = [] } = useSelector((state: RootState) => state.clients);
  const { list: serviceCodes } = useSelector((state: RootState) => state.serviceCodes);

  const [rowInfo, setRowInfo] = useState<ITimesheetTransformedData>();
  const [disabledMinutes, setDisabledMinutes] = useState<number[]>([]);
  const [disabledHours, setDisabledHours] = useState<number[]>([]);
  const [latestPastPayPeriod, setLatestPastPayPeriod] = useState<string[]>([]);
  const [secondLastPayPeriod, setSecondLastPayPeriod] = useState<string[]>([]);
  const [currentPayPeriod, setCurrentPayPeriod] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  useEffect(() => {
    if (!(payPeriods?.past?.length > 0) || !serviceCodes?.length) return;
    const latestPastPayPeriodVal = payPeriods.past[payPeriods.past.length - 1];
    const secondLastPayPeriod = payPeriods.past[payPeriods.past.length - 2];
    setLatestPastPayPeriod(latestPastPayPeriodVal);
    setSecondLastPayPeriod(secondLastPayPeriod);
    setCurrentPayPeriod(payPeriods.present);
  }, [payPeriods, serviceCodes]);

  useEffect(() => {
    if (rowInfo?.id) return;
    setRowInfo({ ...timesheet, client_id: timesheet.client_id ? String(timesheet.client_id) : '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheet]);

  useEffect(() => {
    const hasErrors = Object.values(errors).some((val) => val);
    setIsFormValid(!hasErrors);
  }, [errors]);

  const filteredServiceCodes = useMemo(() => {
    if (!currentProvider?.status) return [];

    const filteredServiceCodes = serviceCodes.filter((serviceCode) => {
      const currentOption: any =
        TIMESHEET_OPTIONS.find((option) => option.label === serviceCode.label) || [];
      const rolesWithAccess = currentOption?.rolesWithAccess || [];
      if (clients?.length === 0 && !currentOption?.isClientOptional) {
        return false;
      }
      let allowed = areUserRolesIn(user?.roles, rolesWithAccess);
      if (allowed && currentOption?.eligibility) {
        allowed = currentOption.eligibility.includes(currentProvider?.status);
      }
      return allowed;
    });
    return filteredServiceCodes;
  }, [serviceCodes, user?.roles, currentProvider?.status, clients?.length]);

  const cstStartTime = momentTz(rowInfo?.date_start).tz(CT_TIMEZONE_AREA);
  const cstEndTime = momentTz(rowInfo?.date_end).tz(CT_TIMEZONE_AREA);

  const [targetedPayPeriod, isFuturePP] = useMemo(() => {
    if (!payPeriods?.present) return [['', ''], false];
    const currentPPStart = momentTz(currentPayPeriod[0]);
    const currentPPEnd = momentTz(currentPayPeriod[1]).endOf('day');
    const lastPPStart = momentTz(latestPastPayPeriod[0]);
    const lastPPEnd = momentTz(latestPastPayPeriod[1]).endOf('day');
    const secondLastPPStart = momentTz(secondLastPayPeriod[0]);
    const secondLastPPEnd = momentTz(secondLastPayPeriod[1]).endOf('day');
    const futurePPStart = momentTz(currentPayPeriod[1]).add(1, 'day');
    const ctStart = moment(cstStartTime.format('YYYY-MM-DD h:mm a'));

    if (ctStart.isSameOrAfter(currentPPStart) && ctStart.isSameOrBefore(currentPPEnd)) {
      return [currentPayPeriod, false];
    }

    if (ctStart.isSameOrAfter(lastPPStart) && ctStart.isSameOrBefore(lastPPEnd)) {
      return [latestPastPayPeriod, false];
    }

    if (ctStart.isSameOrAfter(secondLastPPStart) && ctStart.isSameOrBefore(secondLastPPEnd)) {
      return [secondLastPayPeriod, false];
    }

    if (ctStart.isSameOrAfter(futurePPStart)) {
      return [['', ''], true];
    }

    return [currentPayPeriod, false];
  }, [currentPayPeriod, latestPastPayPeriod, secondLastPayPeriod, payPeriods, cstStartTime]);

  const handleFieldUpdate = (
    field: 'service_code_id' | 'client_id' | 'label',
    value: number | string,
  ) => {
    if (!rowInfo) return;
    const data = { ...rowInfo, [field]: value };
    const clientNameOptional = isClientOptional(serviceCodes, data?.service_code_id ?? '');
    if (field === 'service_code_id' && clientNameOptional) {
      data.client_id = null;
    }
    const errorsData = getAllInvalidFieldNames(getTimesheetSchema(clientNameOptional), data);
    setIsDirty(true);
    setErrors(errorsData);
    setRowInfo(data);
  };

  const validateTimesheet = (startTime: Moment, endTime: Moment) => {
    if (endTime.isSameOrBefore(startTime)) {
      toast.error('Clock out should be later than clock in');
      return false;
    }

    const durationHours = moment.duration(endTime.diff(startTime)).asHours();
    if (durationHours > MAX_TIMESHEET_DURATION_HOURS) {
      toast.error(`Timesheet duration should be maximum of ${MAX_TIMESHEET_DURATION_HOURS} hours`);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!rowInfo) return;
    const clientNameOptional = isClientOptional(serviceCodes, rowInfo?.service_code_id ?? '');
    const isStartEndTimeValid = validateTimesheet(
      moment(rowInfo.date_start),
      moment(rowInfo.date_end),
    );
    const errorsData = getAllInvalidFieldNames(getTimesheetSchema(clientNameOptional), rowInfo);
    if (Object.keys(errorsData).length > 0) {
      setErrors(errorsData);
      return;
    }
    if (!isStartEndTimeValid) return;
    let response;
    if (timesheet?.id) {
      response = await dispatch(
        timesheetActions.update({
          data: { ...rowInfo, client_id: rowInfo?.client_id ? Number(rowInfo?.client_id) : null },
          getAccessTokenSilently,
        }),
      );
    } else {
      response = await dispatch(
        timesheetActions.create({
          data: {
            ...rowInfo,
            client_id: rowInfo?.client_id ? Number(rowInfo?.client_id) : null,
            utc_offset: getUtcOffset(moment(new Date())),
          },
          getAccessTokenSilently,
        }),
      );
    }
    if (response?.error?.message) {
      return;
    }
    dispatch(timesheetActions.updateState({ key: 'removeNewTimesheet', value: true }));
    onClose();
  };

  const disabledDate = (current: Moment) => {
    if (
      current &&
      latestPastPayPeriod.length > 0 &&
      latestPastPayPeriod[0] === selectedPayPeriod[0] &&
      latestPastPayPeriod[1] === selectedPayPeriod[1] &&
      moment(current).isSameOrBefore(
        moment(payPeriods.past_payperiod_data_input_end_date).endOf('day'),
      )
    ) {
      return (
        moment(current).isAfter(moment(latestPastPayPeriod[1]), 'day') ||
        moment(current).isBefore(moment(latestPastPayPeriod[0]), 'day')
      );
    } else {
      // Can not select date and time after current
      return (
        current &&
        (moment(current).isAfter(moment(Date.now()), 'day') ||
          moment(current).isBefore(moment(currentPayPeriod[0]), 'day'))
      );
    }
  };

  const updateDisabledDateTime = (timestamp: Moment) => {
    const { disabledHours, disabledMinutes } = disabledHoursAndMinutesForToday(timestamp);
    setDisabledHours(disabledHours);
    setDisabledMinutes(disabledMinutes);
  };

  const disabledDateTime = () => ({
    disabledHours: () => disabledHours,
    disabledMinutes: () => disabledMinutes,
  });

  const handleTimePickerOpen = (timestamp: Moment) => {
    const { disabledHours, disabledMinutes } = disabledHoursAndMinutesForToday(timestamp);
    setDisabledHours(disabledHours);
    setDisabledMinutes(disabledMinutes);
  };

  const handleDateTimeUpdate = (field: string, value: Moment | null) => {
    if (!rowInfo) return;
    if (!value) return;
    let newDate, startTime, endTime;
    let newDateStart = rowInfo.date_start;
    let newDateEnd = rowInfo.date_end;
    let newValue = value;
    if (
      moment(value).isAfter(moment()) &&
      moment(rowInfo.date_start).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
    ) {
      newValue = moment();
    }
    switch (field) {
      case 'date':
        newDate = value.startOf('day').format('YYYY-MM-DD');
        startTime = moment(rowInfo.date_start).format('HH:mm');
        endTime = moment(rowInfo.date_end).format('HH:mm');
        newDateStart = moment(`${newDate} ${startTime}`);
        newDateEnd = moment(`${newDate} ${endTime}`);
        if (newDateStart.isAfter(moment())) {
          newDateStart = moment().subtract(2, 'hours');
        }
        if (newDateEnd.isAfter(moment())) {
          newDateEnd = moment();
        }
        setRowInfo({ ...rowInfo, date_start: newDateStart, date_end: newDateEnd });
        break;
      case 'date_start':
        newDate = moment(rowInfo?.date_start).startOf('day').format('YYYY-MM-DD');
        startTime = newValue.format('HH:mm');
        newDateStart = moment(`${newDate} ${startTime}`);
        updateDisabledDateTime(newDateStart);
        setRowInfo({ ...rowInfo, date_start: newDateStart });
        break;
      case 'date_end':
        newDate = moment(rowInfo?.date_start).startOf('day').format('YYYY-MM-DD');
        endTime = newValue.format('HH:mm');
        newDateEnd = moment(`${newDate} ${endTime}`);
        updateDisabledDateTime(newDateEnd);
        setRowInfo({ ...rowInfo, date_end: newDateEnd });
        break;
      default:
        break;
    }
    setIsDirty(true);
  };

  return (
    <TableRow sx={{ width: '100% !important' }}>
      <TableCell colSpan={12}>
        <Box
          sx={{
            borderRadius: 10,
            padding: 2,
            backgroundColor: FORTA_WHITE,
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex' }} mb={3}>
            <Typography color={FORTA_BLUE.primary} fontWeight="bold" sx={{ fontSize: 21 }} mr={4}>
              Non-therapy hours
            </Typography>
            {!isFormValid && (
              <>
                <img
                  style={{
                    width: '22px',
                    height: '22px',
                    marginRight: '8px',
                  }}
                  src={ErrorAlertIcon}
                  alt="error-alert-icon"
                />
                <Typography color={isFormValid ? FORTA_BLUE.light1 : FORTA_ERROR}>
                  All fields are required
                </Typography>
              </>
            )}
          </Box>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={3}>
              <Typography fontWeight="bold" mb={1}>
                Type
              </Typography>
              <FHSelect
                data-testid="therapyTypeDropDown"
                sx={{ width: '100%', boxShadow: 'none' }}
                inputHeight={40}
                backgroundType={BackgroundTypes.Colored}
                menuProps={{
                  '& .MuiPaper-elevation': {
                    boxShadow: `0px 4px 4px ${FORTA_BLUE.light2}`,
                  },
                }}
                options={filteredServiceCodes.map((serviceCode: IServiceCode) => ({
                  value: serviceCode.id ?? '',
                  label: serviceCode.label,
                }))}
                value={rowInfo?.service_code_id || filteredServiceCodes?.[0]?.['id'] || ''}
                onUpdate={(value: SelectValueType) => {
                  handleFieldUpdate('service_code_id', value);
                }}
                error={errors['service_code_id']}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight="bold" mb={1}>
                Client Name
              </Typography>
              <FHSelect
                data-testid="clientNameDropDown"
                sx={{ width: '100%', boxShadow: 'none' }}
                inputHeight={40}
                menuProps={{
                  '& .MuiPaper-elevation': {
                    boxShadow: `0px 4px 4px ${FORTA_BLUE.light2}`,
                  },
                }}
                backgroundType={BackgroundTypes.Colored}
                options={clients.map((client: IClient) => ({
                  value: client.id ?? '',
                  label: `${client.first_name} ${client.last_name}`,
                }))}
                disabled={isClientOptional(serviceCodes, rowInfo?.service_code_id ?? '')}
                value={rowInfo?.client_id || null}
                onUpdate={(value: SelectValueType) => {
                  handleFieldUpdate('client_id', value);
                }}
                error={errors['client_id']}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography fontWeight="bold" mb={1}>
                Date
              </Typography>
              <FHAntdDateTimePicker
                data-testid="datePickerNonTherapyHours"
                coloredBackground
                allowClear={false}
                format="MM/DD/YYYY"
                inputHeight={40}
                style={{ width: '100%', boxShadow: 'none' }}
                disabledDate={disabledDate}
                value={rowInfo?.date_start ? moment(rowInfo.date_start) : ''}
                onChange={(value: Moment) => {
                  handleDateTimeUpdate('date', value);
                }}
                onSelect={(value: Moment) => {
                  handleDateTimeUpdate('date', value);
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography fontWeight="bold" mb={1}>
                Start time
              </Typography>
              <FAntdTimePicker
                data-testid="startTimePickerNonTherapyHours"
                use12Hours
                allowClear={false}
                format="h:mm a"
                inputHeight={40}
                style={{ width: '100%', boxShadow: 'none' }}
                value={rowInfo?.date_start ? moment(rowInfo.date_start) : undefined}
                disabledTime={disabledDateTime}
                onSelect={(value: Moment) => {
                  handleDateTimeUpdate('date_start', value);
                }}
                onChange={(value: Moment | null) => {
                  handleDateTimeUpdate('date_start', value);
                }}
                onOpenChange={() => handleTimePickerOpen(moment(rowInfo?.date_start))}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography fontWeight="bold" mb={1}>
                End time
              </Typography>
              <FAntdTimePicker
                data-testid="endTimePickerNonTherapyHours"
                use12Hours
                allowClear={false}
                format="h:mm a"
                inputHeight={40}
                style={{ width: '100%', boxShadow: 'none' }}
                disabledTime={disabledDateTime}
                value={rowInfo?.date_end ? moment(rowInfo.date_end) : undefined}
                onSelect={(value: Moment) => {
                  handleDateTimeUpdate('date_end', value);
                }}
                onChange={(value: Moment | null) => {
                  handleDateTimeUpdate('date_end', value);
                }}
                onOpenChange={() => handleTimePickerOpen(moment(rowInfo?.date_end))}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: FORTA_BLUE.light3,
                  borderRadius: '10px',
                }}
              >
                <Typography fontWeight={700} mb={2} color={FORTA_BLUE.primary}>
                  Pay Period Information
                </Typography>
                {isFuturePP ? (
                  <Box sx={{ display: 'flex' }}>
                    <InfoOutlinedIcon
                      sx={{ color: FORTA_ERROR, mr: 0.5, height: 18, width: 18, mt: 0.4 }}
                    />
                    <Typography fontWeight={700} mr={1} color={FORTA_ERROR}>
                      Note:
                    </Typography>
                    <Typography color={FORTA_ERROR}>
                      Cannot add time entry in future pay period
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex' }}>
                      <InfoOutlinedIcon
                        sx={{ color: FORTA_ERROR, mr: 0.5, height: 18, width: 18, mt: 0.4 }}
                      />
                      <Typography color={FORTA_ERROR}>
                        Note: This entry will be added in the period shown below
                      </Typography>
                    </Box>
                    <Box mt={2}>
                      <Typography>{`Pay Period: ${moment(targetedPayPeriod[0]).format(
                        'MM/DD/YYYY',
                      )} - ${moment(targetedPayPeriod[1]).format('MM/DD/YYYY')}`}</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ border: `2px solid ${FORTA_BLUE.light3}`, p: 2, borderRadius: '10px' }}>
                <Typography fontWeight={700} mb={2}>
                  Time Zone Conversion
                </Typography>
                <Box sx={{ display: 'flex' }}>
                  <AccessTimeOutlinedIcon
                    sx={{ height: 18, width: 18, color: FORTA_BLUE.primary, mt: 0.3, mr: 0.5 }}
                  />
                  <Typography
                    color={FORTA_BLUE.primary}
                    mb={2}
                  >{`All pay periods are processed in Central Time (${momentTz()
                    .tz(CT_TIMEZONE_AREA)
                    .format('z')})`}</Typography>
                </Box>
                <Box>
                  <Typography mb={1}>{`Start Time (${momentTz()
                    .tz(CT_TIMEZONE_AREA)
                    .format('z')}): `}</Typography>
                  <Box
                    sx={{
                      p: 1,
                      border: `2px solid ${FORTA_GRAY2}`,
                      borderRadius: '10px',
                      cursor: 'not-allowed',
                    }}
                  >
                    <Typography>{cstStartTime.format('MM/DD/YYYY - h:mm a')}</Typography>
                  </Box>
                </Box>
                <Box sx={{ pt: 2 }}>
                  <Typography mb={1}>{`End Time (${momentTz()
                    .tz(CT_TIMEZONE_AREA)
                    .format('z')}): `}</Typography>
                  <Box
                    sx={{
                      p: 1,
                      border: `2px solid ${FORTA_GRAY2}`,
                      borderRadius: '10px',
                      cursor: 'not-allowed',
                    }}
                  >
                    <Typography>{cstEndTime.format('MM/DD/YYYY - h:mm a')}</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', flexDirection: 'column' }} mb={3}>
            <Typography fontWeight="bold" mb={1}>
              Description of time logged
            </Typography>
            <FHInputField
              data-testid="descriptionTimeLogged"
              coloredBackground
              fullWidth
              inputHeight={40}
              sx={{ boxShadow: 'none' }}
              value={rowInfo?.label || ''}
              onChange={(e) => {
                handleFieldUpdate('label', e.target.value);
              }}
              error={errors['label']}
            />
            <Typography color={FORTA_ERROR} fontSize={14} align="right">
              {errors['label'] && 'Description is required and must be at least 60 characters'}
            </Typography>
          </Box>
          <Box>
            <FHButton
              data-testid="cancelNonTherapyHoursLogging"
              sx={{
                mr: 3,
                backgroundColor: FORTA_WHITE,
                color: FORTA_GRAY4,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: FORTA_BLUE.light2,
                ':hover': {
                  backgroundColor: FORTA_WHITE,
                  color: FORTA_GRAY4,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: FORTA_BLUE.light2,
                },
              }}
              onClick={onClose}
            >
              Cancel
            </FHButton>
            <FHButton
              data-testid="addNonTherapyHoursLogging"
              disabled={!isDirty || !isFormValid || isFuturePP}
              colorTheme={Themes.Primary}
              onClick={handleSave}
            >
              {timesheet?.id ? 'Save Edit' : 'Save Entry'}
            </FHButton>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default TimesheetEditRow;
